import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getReadingProgress,
  getAllReadingProgress,
  saveReadingProgress,
  type ReadingProgress,
} from '../lib/offlineStorage';

export const readingProgressKeys = {
  all: ['reading-progress'] as const,
  list: () => [...readingProgressKeys.all, 'list'] as const,
  detail: (bookId: number) => [...readingProgressKeys.all, 'detail', bookId] as const,
};

export function useReadingProgress(bookId: number) {
  return useQuery({
    queryKey: readingProgressKeys.detail(bookId),
    queryFn: () => getReadingProgress(bookId),
    staleTime: Infinity,
  });
}

export function useAllReadingProgress() {
  return useQuery({
    queryKey: readingProgressKeys.list(),
    queryFn: getAllReadingProgress,
    staleTime: 30 * 1000,
  });
}

export function useReadingProgressMap() {
  const { data: progressList = [] } = useAllReadingProgress();

  const progressMap = new Map<number, ReadingProgress>();
  for (const progress of progressList) {
    progressMap.set(progress.bookId, progress);
  }

  return progressMap;
}

export function useSaveReadingProgress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      bookId,
      cfi,
      percentage,
    }: {
      bookId: number;
      cfi: string;
      percentage: number;
    }) => saveReadingProgress(bookId, cfi, percentage),
    onSuccess: (_, { bookId, cfi, percentage }) => {
      const progress: ReadingProgress = {
        bookId,
        cfi,
        percentage,
        updatedAt: Date.now(),
      };
      queryClient.setQueryData(readingProgressKeys.detail(bookId), progress);
      queryClient.invalidateQueries({ queryKey: readingProgressKeys.list() });
    },
  });
}
